// Торговые отчёты — jurnalul zilei.
//
// Regula lor: o publicare pe zi calendaristică. Am ales ca a doua publicare din aceeași
// zi s-o **înlocuiască** pe prima, nu s-o refuze: cine greșește o cifră la ora 14 trebuie
// s-o poată corecta seara, nu să rămână cu ea până mâine.

import { el, clear, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { session, initials } from '../core/session.js';
import { openAuth } from '../ui/auth-modal.js';
import { guestGate } from '../ui/guest-gate.js';
import { modal, toast } from '../ui/components.js';

function ziLabel(day) {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d))
    .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export class ReportsSection {
  constructor(app) {
    this.app = app;
    this.items = [];
  }

  async mount(host) {
    this.root = el('div', { class: 'ideas-section' });
    this.listEl = el('div', { class: 'ideas-list' });

    this.root.append(
      el('div', { class: 'ideas-head' }, [
        el('h2', { class: 'ideas-title', text: 'Торговые отчёты' }),
        el('span', { class: 'reports-hint', text: 'Один отчёт в календарный день' }),
        el('button', {
          class: 'btn primary ideas-new',
          onclick: () => this.#openForm(),
        }, [icon('plus', 12), el('span', { text: 'Отчёт за день' })]),
      ]),
      this.listEl,
    );
    host.append(this.root);

    this.offSession = bus.on('session:change', () => this.#load());
    await this.#load();
  }

  async #load() {
    try {
      const data = await (await fetch('/api/reports')).json();
      this.items = data.items || [];
    } catch {
      this.items = [];
    }
    this.#render();
  }

  #render() {
    clear(this.listEl);
    if (!this.items.length) {
      this.listEl.append(session.authenticated
        ? el('div', { class: 'ideas-empty' }, [
          el('div', { text: 'Отчётов пока нет.' }),
          el('div', { class: 'ideas-empty-sub', text: 'Итоги дня со скриншотом сделок — самый простой способ увидеть свой прогресс.' }),
        ])
        : guestGate('Здесь участники подводят итоги торгового дня. Войдите, чтобы вести свой журнал.'));
      return;
    }
    for (const r of this.items) this.listEl.append(this.#card(r));
  }

  #card(r) {
    const plus = r.pnl >= 0;
    return el('article', { class: `idea-card ${plus ? 'worked' : 'failed'}` }, [
      el('div', { class: 'idea-head' }, [
        el('span', {
          class: 'chat-avatar', text: initials(r.author.username),
          style: { background: r.author.status.color },
        }),
        el('div', { class: 'idea-who' }, [
          el('button', {
            class: 'idea-author', text: r.author.username,
            style: { color: r.author.status.color },
            onclick: () => bus.emit('profile:open', { username: r.author.username }),
          }),
          el('span', { class: 'idea-when', text: ziLabel(r.day) }),
        ]),
      ]),

      el('div', { class: 'rep-nums' }, [
        el('div', { class: `rep-num ${plus ? 'up' : 'down'}` }, [
          el('span', { class: 'rep-num-v', text: `${plus ? '+' : ''}${r.pnl}` }),
          el('span', { class: 'rep-num-k', text: 'Результат, $' }),
        ]),
        el('div', { class: 'rep-num' }, [
          el('span', { class: 'rep-num-v', text: String(r.trades) }),
          el('span', { class: 'rep-num-k', text: 'Сделок' }),
        ]),
      ]),

      r.shot
        ? el('img', {
          class: 'idea-shot', src: r.shot, alt: '',
          onclick: () => window.open(r.shot, '_blank'),
        })
        : null,
      r.text ? el('p', { class: 'idea-text', text: r.text }) : null,
    ]);
  }

  async #openForm() {
    if (!session.authenticated) { openAuth(); return; }

    // Dacă azi există deja un raport, formularul se deschide cu el — ca să se vadă că
    // îl corectezi, nu că adaugi încă unul.
    let existent = null;
    try {
      existent = (await (await fetch('/api/reports/today')).json()).report;
    } catch { /* mergem cu formular gol */ }

    const pnlEl = el('input', { class: 'auth-input', type: 'number', step: 'any', placeholder: '0', value: existent?.pnl ?? '' });
    const tradesEl = el('input', { class: 'auth-input', type: 'number', step: '1', placeholder: '0', value: existent?.trades ?? '' });
    const textEl = el('textarea', {
      class: 'idea-form-text', rows: 4,
      placeholder: 'Что сработало, что нет, какие ошибки — для себя через месяц.',
      value: existent?.text || '',
    });

    let shot = existent?.shot || '';
    const previewEl = el('div', { class: 'rep-shot' });
    const paint = () => {
      clear(previewEl);
      if (shot) previewEl.append(el('img', { class: 'share-preview', src: shot, alt: '' }));
    };
    paint();

    const fileEl = el('input', {
      type: 'file', accept: 'image/*', style: { display: 'none' },
      onchange: (e) => this.#readShot(e.target.files[0], (d) => { shot = d; paint(); }),
    });

    const errEl = el('div', { class: 'auth-error' });
    const body = el('div', { class: 'idea-form' }, [
      existent ? el('div', { class: 'rep-exists', text: 'За сегодня отчёт уже есть — он будет обновлён.' }) : null,
      el('div', { class: 'idea-form-row' }, [
        el('label', { class: 'auth-field grow' }, [icon('coins', 12), pnlEl]),
        el('label', { class: 'auth-field grow' }, [icon('bolt', 12), tradesEl]),
      ]),
      el('div', { class: 'idea-form-label', text: 'Скриншот сделок' }),
      el('label', { class: 'btn rep-attach' }, [icon('camera', 12), el('span', { text: 'Прикрепить' }), fileEl]),
      previewEl,
      textEl,
      errEl,
    ]);

    modal({
      title: 'Итоги дня',
      width: 520,
      body,
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: existent ? 'Обновить' : 'Опубликовать', primary: true,
          onClick: async (c) => {
            errEl.textContent = '';
            try {
              const resp = await fetch('/api/reports', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  pnl: pnlEl.value, trades: tradesEl.value, text: textEl.value, shot,
                }),
              });
              const data = await resp.json();
              if (!resp.ok) throw new Error(data.detail);
              c();
              toast('Отчёт сохранён', 'success');
              this.#load();
            } catch (err) {
              errEl.textContent = err.message || 'Не удалось сохранить';
            }
          },
        },
      ],
    });
  }

  /** Imaginea se micșorează în browser: 400 KB e limita serverului, telefoanele dau mult mai mult. */
  #readShot(file, gata) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1280 / img.width);
        const cv = document.createElement('canvas');
        cv.width = Math.round(img.width * scale);
        cv.height = Math.round(img.height * scale);
        cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
        gata(cv.toDataURL('image/jpeg', 0.72));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  unmount() {
    this.offSession?.();
    this.root?.remove();
  }
}
